/**
 * Avatar frames — decorative rings shown around an equipped avatar so friends
 * see a fun border around a person's picture. Kept in sync with the backend
 * catalog in routes/marketplace_shop/creator.py (_AVATAR_FRAMES).
 * Rings use layered box-shadows so they follow the circular border-radius and
 * never change the element's layout size.
 */
import React from "react";

export const FRAME_OPTIONS = [
  { id: "none", label: "No frame", emoji: "⬜" },
  { id: "starry", label: "Starry", emoji: "✨" },
  { id: "gold", label: "Gold", emoji: "🥇" },
  { id: "birthday", label: "Birthday", emoji: "🎂" },
  { id: "neon", label: "Neon", emoji: "💠" },
  { id: "rainbow", label: "Rainbow", emoji: "🌈" },
  { id: "snow", label: "Snow", emoji: "❄️" },
  { id: "hearts", label: "Hearts", emoji: "💖" },
  { id: "fireworks", label: "Fireworks", emoji: "🎆" },
];

const RINGS = {
  none: "none",
  starry: "0 0 0 3px #FDE68A, 0 0 16px 3px rgba(250,204,21,0.75)",
  gold: "0 0 0 3px #F59E0B, 0 0 14px 2px rgba(245,158,11,0.7)",
  birthday: "0 0 0 3px #F472B6, 0 0 0 6px #22D3EE, 0 0 16px 3px rgba(244,114,182,0.55)",
  neon: "0 0 0 3px #22D3EE, 0 0 16px 3px rgba(34,211,238,0.8)",
  rainbow: "0 0 0 3px #F87171, 0 0 0 6px #FBBF24, 0 0 0 9px #34D399, 0 0 0 12px #60A5FA",
  snow: "0 0 0 3px #BAE6FD, 0 0 16px 3px rgba(186,230,253,0.8)",
  hearts: "0 0 0 3px #FB7185, 0 0 16px 3px rgba(251,113,133,0.75)",
  fireworks: "0 0 0 3px #FBBF24, 0 0 0 6px #F472B6, 0 0 16px 3px rgba(244,114,182,0.6)",
};

export function getFrameStyle(frameId) {
  const ring = RINGS[frameId];
  if (!frameId || frameId === "none" || !ring) return {};
  return { boxShadow: ring };
}

/**
 * Soft glow/scene shown BEHIND a see-through (transparent) cut-out avatar so it
 * pops cleanly inside its frame. Applied to the circular wrapper behind the
 * image — opaque photos cover it, transparent PNG cut-outs float on top of it.
 * Each frame gets a coordinated backdrop that matches its ring colors.
 */
const BACKDROPS = {
  none: "radial-gradient(circle at 50% 38%, #334155 0%, #1e293b 100%)",
  starry: "radial-gradient(circle at 50% 35%, #4c1d95 0%, #1e1b4b 70%, #0b1020 100%)",
  gold: "radial-gradient(circle at 50% 35%, #78350f 0%, #451a03 70%, #1c1207 100%)",
  birthday: "radial-gradient(circle at 50% 32%, #831843 0%, #164e63 75%, #0b1020 100%)",
  neon: "radial-gradient(circle at 50% 35%, #164e63 0%, #083344 70%, #06121a 100%)",
  rainbow: "radial-gradient(circle at 50% 35%, #4338ca 0%, #831843 55%, #064e3b 100%)",
  snow: "radial-gradient(circle at 50% 32%, #1e3a5f 0%, #0f2540 70%, #081525 100%)",
  hearts: "radial-gradient(circle at 50% 34%, #7f1d3a 0%, #4a0f24 70%, #1c0710 100%)",
  fireworks: "radial-gradient(circle at 50% 40%, #1e1b4b 0%, #2a0f3d 60%, #0b1020 100%)",
};

export function getFrameBackdropStyle(frameId) {
  const bg = BACKDROPS[frameId] || BACKDROPS.none;
  return { background: bg };
}

// ── Animated frame scenes ───────────────────────────────────────────────
// A lightweight, CSS-only animated layer that sits BEHIND the avatar so a
// see-through cut-out (or sticker) floats over a living scene: twinkling
// stars, falling confetti, a neon pulse, a gold shimmer, or a rainbow sweep.
// Injected once; safe to render in many small tiles (pure transforms/opacity).
let _iwFramesKf = false;
export function ensureFrameKeyframes() {
  if (_iwFramesKf || typeof document === "undefined") return;
  _iwFramesKf = true;
  const el = document.createElement("style");
  el.setAttribute("data-iw-frame-scenes", "1");
  el.textContent = `
@keyframes iwTwinkle {0%,100%{opacity:.2;transform:scale(.7)}50%{opacity:1;transform:scale(1.2)}}
@keyframes iwConfetti {0%{transform:translateY(-140%) rotate(0);opacity:0}12%{opacity:1}100%{transform:translateY(150%) rotate(300deg);opacity:0}}
@keyframes iwNeon {0%,100%{opacity:.3}50%{opacity:.85}}
@keyframes iwSpin {to{transform:rotate(360deg)}}
@keyframes iwHue {to{filter:hue-rotate(360deg)}}
@keyframes iwBob {0%,100%{transform:translateY(0)}50%{transform:translateY(-4%)}}
@keyframes iwFall {0%{transform:translateY(-30%);opacity:0}15%{opacity:1}100%{transform:translateY(150%);opacity:.2}}
@keyframes iwFloatUp {0%{transform:translateY(120%) scale(.6);opacity:0}20%{opacity:1}100%{transform:translateY(-30%) scale(1);opacity:0}}
@keyframes iwBurst {0%{transform:scale(0);opacity:0}30%{opacity:1}70%{opacity:.9}100%{transform:scale(1.6);opacity:0}}`;
  document.head.appendChild(el);
}

const _layer = { position: "absolute", inset: 0, borderRadius: "inherit", overflow: "hidden", pointerEvents: "none" };

// Viewer-level "calm animations" preference (Sparkle Toggle). When a parent/kid
// turns animations off, scenes + bob go still everywhere for that viewer.
export function animationsOn() {
  try { return localStorage.getItem("iw_animations") !== "0"; } catch { return true; }
}

/** Animated scene rendered behind an avatar for the given frame. Returns null
 *  for "none"/unknown so plain avatars stay calm. */
export function FrameScene({ frameId }) {
  ensureFrameKeyframes();
  if (!frameId || frameId === "none" || !animationsOn()) return null;

  if (frameId === "starry") {
    const stars = [[18, 22], [72, 30], [40, 70], [82, 68], [30, 46]];
    return (
      <span style={_layer} aria-hidden data-testid="frame-scene-starry">
        {stars.map(([x, y], i) => (
          <span key={i} style={{ position: "absolute", left: `${x}%`, top: `${y}%`, width: 4, height: 4, borderRadius: "50%", background: "#FDE68A", boxShadow: "0 0 6px 1px rgba(253,230,138,0.9)", animation: `iwTwinkle ${1.6 + i * 0.3}s ease-in-out ${i * 0.25}s infinite` }} />
        ))}
      </span>
    );
  }
  if (frameId === "birthday") {
    const cols = ["#F472B6", "#22D3EE", "#FDE68A", "#A78BFA", "#34D399"];
    return (
      <span style={_layer} aria-hidden data-testid="frame-scene-birthday">
        {cols.map((c, i) => (
          <span key={i} style={{ position: "absolute", left: `${12 + i * 19}%`, top: "-10%", width: 5, height: 8, borderRadius: 1, background: c, animation: `iwConfetti ${1.8 + (i % 3) * 0.5}s linear ${i * 0.3}s infinite` }} />
        ))}
      </span>
    );
  }
  if (frameId === "neon") {
    return (
      <span style={_layer} aria-hidden data-testid="frame-scene-neon">
        <span style={{ position: "absolute", inset: 2, borderRadius: "inherit", boxShadow: "inset 0 0 10px 2px rgba(34,211,238,0.8)", animation: "iwNeon 1.4s ease-in-out infinite" }} />
      </span>
    );
  }
  if (frameId === "gold") {
    return (
      <span style={_layer} aria-hidden data-testid="frame-scene-gold">
        <span style={{ position: "absolute", left: "-50%", top: "-50%", width: "200%", height: "200%", background: "conic-gradient(from 0deg, transparent 0deg, rgba(253,230,138,0.55) 40deg, transparent 90deg, transparent 360deg)", animation: "iwSpin 3.4s linear infinite" }} />
      </span>
    );
  }
  if (frameId === "rainbow") {
    return (
      <span style={_layer} aria-hidden data-testid="frame-scene-rainbow">
        <span style={{ position: "absolute", inset: 0, background: "conic-gradient(#F87171,#FBBF24,#34D399,#60A5FA,#A78BFA,#F87171)", opacity: 0.5, animation: "iwHue 4s linear infinite" }} />
      </span>
    );
  }
  if (frameId === "snow") {
    const flakes = [[15, 0], [35, 0], [55, 0], [75, 0], [90, 0], [25, 0]];
    return (
      <span style={_layer} aria-hidden data-testid="frame-scene-snow">
        {flakes.map(([x], i) => (
          <span key={i} style={{ position: "absolute", left: `${x}%`, top: "-10%", fontSize: 8, lineHeight: 1, color: "#E0F2FE", textShadow: "0 0 4px rgba(186,230,253,0.9)", animation: `iwFall ${2.2 + (i % 3) * 0.7}s linear ${i * 0.35}s infinite` }}>❄</span>
        ))}
      </span>
    );
  }
  if (frameId === "hearts") {
    const hearts = [[20, "#FB7185"], [45, "#F472B6"], [68, "#FDA4AF"], [85, "#FB7185"]];
    return (
      <span style={_layer} aria-hidden data-testid="frame-scene-hearts">
        {hearts.map(([x, c], i) => (
          <span key={i} style={{ position: "absolute", left: `${x}%`, bottom: "-12%", fontSize: 9, lineHeight: 1, color: c, animation: `iwFloatUp ${2.4 + (i % 3) * 0.6}s ease-in ${i * 0.4}s infinite` }}>♥</span>
        ))}
      </span>
    );
  }
  if (frameId === "fireworks") {
    const bursts = [[30, 32, "#FBBF24"], [68, 40, "#F472B6"], [50, 62, "#60A5FA"]];
    return (
      <span style={_layer} aria-hidden data-testid="frame-scene-fireworks">
        {bursts.map(([x, y, c], i) => (
          <span key={i} style={{ position: "absolute", left: `${x}%`, top: `${y}%`, width: 10, height: 10, borderRadius: "50%", background: `radial-gradient(circle, ${c} 0%, transparent 70%)`, boxShadow: `0 0 8px 2px ${c}`, animation: `iwBurst ${1.6 + i * 0.4}s ease-out ${i * 0.5}s infinite` }} />
        ))}
      </span>
    );
  }
  return null;
}
